import { useAudioTrack, useDaily, useDailyEvent, useDevices, useLocalSessionId, useVideoTrack } from "@daily-co/daily-react";
import { useCallback, useEffect, useState, useMemo } from "react";
import { Mic, Video, Volume2, AlertCircle, Waves } from 'lucide-react';
import SelectDevice from "./ui/SelectDevice";
import { Button } from "./ui/Button";
import React from "react";

const DeviceSection = React.memo(({ title, value, devices, onChange, Icon }) => (
    <div className="space-y-4">
        <h3 className="text-lg font-medium text-white">{title}</h3>
        <div className="flex items-center space-x-4">
            <SelectDevice
                value={value}
                devices={devices}
                onChange={onChange}
                Icon={Icon}
            />
        </div>
    </div>
));

DeviceSection.displayName = 'DeviceSection';

export default function CameraSettings({ actionLabel, onAction, cancelLabel, onCancel, onClose }) {
    const daily = useDaily();
    const {
        currentCam,
        currentMic,
        currentSpeaker,
        microphones,
        speakers,
        cameras,
        setMicrophone,
        setCamera,
        setSpeaker,
        refreshDevices,
    } = useDevices();
    const localSessionID = useLocalSessionId();
    const localVideo = useVideoTrack(localSessionID);
    const localAudio = useAudioTrack(localSessionID);
    const isCameraEnabled = !localVideo.isOff;
    const isMicEnabled = !localAudio.isOff;
    const isSpeakerEnabled = !localAudio.isOff;
    const [getUserMediaError, setGetUserMediaError] = useState(false);
    const [permissionState, setPermissionState] = useState({
        camera: 'prompt',
        microphone: 'prompt'
    });
    const [selectedCamera, setSelectedCamera] = useState(null);
    const [selectedMic, setSelectedMic] = useState(null);
    const [selectedSpeaker, setSelectedSpeaker] = useState(null);
    
    // Initialize noise cancellation state from localStorage or default to true
    const [noiseCancellation, setNoiseCancellation] = useState(() => {
        // Try to get the saved preference from localStorage
        const savedPreference = localStorage.getItem('noiseCancellation');
        // If there's a saved preference, use it; otherwise default to true
        return savedPreference !== null ? savedPreference === 'true' : true;
    });

    // Save noise cancellation preference to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('noiseCancellation', noiseCancellation.toString());
    }, [noiseCancellation]);

    // Memoize device lists to prevent unnecessary re-renders
    const deviceLists = useMemo(() => {
        const getDeviceName = (device, type) => {
            if (device.label) return device.label;
            if (device.deviceId === 'default') return `Default ${type}`;
            return `${type} (${device.deviceId.slice(0, 8)})`;
        };

        return {
            cameras: Array.isArray(cameras) ? cameras.map(device => ({
                deviceId: device.device?.deviceId || device.deviceId,
                label: getDeviceName(device.device || device, 'Camera'),
                kind: 'videoinput'
            })) : [],
            microphones: Array.isArray(microphones) ? microphones.map(device => ({
                deviceId: device.device?.deviceId || device.deviceId,
                label: getDeviceName(device.device || device, 'Microphone'),
                kind: 'audioinput'
            })) : [],
            speakers: Array.isArray(speakers) ? speakers.map(device => ({
                deviceId: device.device?.deviceId || device.deviceId,
                label: getDeviceName(device.device || device, 'Speaker'),
                kind: 'audiooutput'
            })) : []
        };
    }, [cameras, microphones, speakers]);

    // Initialize selected devices when component mounts
    useEffect(() => {
        const initializeDevices = () => {
            // For camera
            if (currentCam) {
                const deviceId = currentCam.device?.deviceId || currentCam.deviceId;
                setSelectedCamera(deviceId);
            } else if (deviceLists.cameras.length > 0) {
                setSelectedCamera(deviceLists.cameras[0].deviceId);
            }

            // For microphone
            if (currentMic) {
                const deviceId = currentMic.device?.deviceId || currentMic.deviceId;
                setSelectedMic(deviceId);
            } else if (deviceLists.microphones.length > 0) {
                setSelectedMic(deviceLists.microphones[0].deviceId);
            }

            // For speaker
            if (currentSpeaker) {
                const deviceId = currentSpeaker.device?.deviceId || currentSpeaker.deviceId;
                setSelectedSpeaker(deviceId);
            } else if (deviceLists.speakers.length > 0) {
                setSelectedSpeaker(deviceLists.speakers[0].deviceId);
            }
        };

        initializeDevices();
    }, [currentCam, currentMic, currentSpeaker, deviceLists]);

    // Update selected devices when available devices change
    useEffect(() => {
        const updateDevices = async () => {
            if (permissionState.camera === 'granted' && permissionState.microphone === 'granted') {
                await refreshDevices();
            }
        };

        updateDevices();
    }, [refreshDevices, permissionState]);

    // Check permissions on mount
    useEffect(() => {
        const checkPermissions = async () => {
            try {
                const permissions = await Promise.all([
                    navigator.permissions.query({ name: 'camera' }),
                    navigator.permissions.query({ name: 'microphone' })
                ]);
                
                setPermissionState({
                    camera: permissions[0].state,
                    microphone: permissions[1].state
                });

                // If permissions are granted, initialize devices
                if (permissions[0].state === 'granted' && permissions[1].state === 'granted') {
                    await refreshDevices();
                }
            } catch (error) {
                console.error('Error checking permissions:', error);
                setGetUserMediaError(true);
            }
        };

        checkPermissions();
    }, [refreshDevices]);

    // Request permissions when needed
    const requestPermissions = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ 
                video: true, 
                audio: true 
            });
            
            // Stop the stream immediately as we just needed it for permission
            stream.getTracks().forEach(track => track.stop());
            
            // Refresh devices after permissions are granted
            await refreshDevices();
            
            // Update permission states
            setPermissionState({
                camera: 'granted',
                microphone: 'granted'
            });
            
            setGetUserMediaError(false);
        } catch (error) {
            console.error('Error requesting permissions:', error);
            setGetUserMediaError(true);
            
            // Update permission states based on error
            if (error.name === 'NotAllowedError') {
                setPermissionState({
                    camera: 'denied',
                    microphone: 'denied'
                });
            }
        }
    };

    const handleCameraError = useCallback(() => {
        setGetUserMediaError(true);
    }, []);

    useDailyEvent('camera-error', handleCameraError);
    
    const handleApply = useCallback(() => {
        if (selectedCamera && selectedCamera !== currentCam?.device?.deviceId) {
            setCamera(selectedCamera);
        }
        if (selectedMic && selectedMic !== currentMic?.device?.deviceId) {
            setMicrophone(selectedMic);
        }
        if (selectedSpeaker && selectedSpeaker !== currentSpeaker?.device?.deviceId) {
            setSpeaker(selectedSpeaker);
        }
        
        // Apply noise cancellation settings with the correct format
        if (daily) {
            daily.updateInputSettings({
                audio: {
                    processor: {
                        type: noiseCancellation ? 'noise-cancellation' : 'none'
                    }
                }
            });
        }
        
        onAction?.();
        onClose?.();
    }, [selectedCamera, selectedMic, selectedSpeaker, currentCam, currentMic, currentSpeaker, setCamera, setMicrophone, setSpeaker, onAction, onClose, daily, noiseCancellation]);

    const handleCancel = useCallback(() => {
        onCancel?.();
        onClose?.();
    }, [onCancel, onClose]);

    const isApplyDisabled = useMemo(() => 
        getUserMediaError || !selectedCamera || !selectedMic || 
        permissionState.camera === 'denied' || permissionState.microphone === 'denied',
        [getUserMediaError, selectedCamera, selectedMic, permissionState]
    );
    
    return (
        <div className="flex flex-col space-y-6 p-6 bg-slate-800 rounded-lg">
            {(getUserMediaError || permissionState.camera === 'denied' || permissionState.microphone === 'denied') && (
                <div className="text-center space-y-4">
                    <div className="flex items-center justify-center space-x-2 text-red-400">
                        <AlertCircle className="w-5 h-5" />
                        <p className="font-medium">Camera or microphone access denied</p>
                    </div>
                    <div className="space-y-2">
                        {permissionState.camera === 'denied' && (
                            <p className="text-sm text-slate-300">Please enable camera access in your browser settings</p>
                        )}
                        {permissionState.microphone === 'denied' && (
                            <p className="text-sm text-slate-300">Please enable microphone access in your browser settings</p>
                        )}
                    </div>
                    <Button 
                        onClick={requestPermissions} 
                        className="px-6 py-2 rounded-full bg-primary text-white hover:bg-primary/90"
                    >
                        Request Permissions
                    </Button>
                </div>
            )}
            
            {!getUserMediaError && permissionState.camera !== 'denied' && permissionState.microphone !== 'denied' && (
                <>
                    <DeviceSection
                        title="Camera"
                        value={selectedCamera}
                        devices={deviceLists.cameras}
                        onChange={setSelectedCamera}
                        Icon={Video}
                    />
                    
                    <DeviceSection
                        title="Microphone"
                        value={selectedMic}
                        devices={deviceLists.microphones}
                        onChange={setSelectedMic}
                        Icon={Mic}
                    />
                    
                    <DeviceSection
                        title="Speaker"
                        value={selectedSpeaker}
                        devices={deviceLists.speakers}
                        onChange={setSelectedSpeaker}
                        Icon={Volume2}
                    />
                    
                    {/* Noise Cancellation Settings */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-medium text-white">Audio Processing</h3>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                                <Waves className="w-5 h-5 text-white" />
                                <span className="text-white">Noise Cancellation</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        className="sr-only peer" 
                                        checked={noiseCancellation}
                                        onChange={(e) => setNoiseCancellation(e.target.checked)}
                                    />
                                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                </label>
                            </div>
                        </div>
                        
                        {noiseCancellation && (
                            <div className="text-sm text-slate-300">
                                Noise cancellation is enabled. This will reduce background noise during your call.
                            </div>
                        )}
                    </div>
                </>
            )}
            
            <div className="flex justify-end space-x-4 mt-6">
                {cancelLabel && (
                    <Button
                        variant="outline"
                        onClick={handleCancel}
                        className="px-4 py-2"
                    >
                        {cancelLabel}
                    </Button>
                )}
                {actionLabel && (
                    <Button
                        onClick={handleApply}
                        disabled={isApplyDisabled}
                        className="px-4 py-2 bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {actionLabel}
                    </Button>
                )}
            </div>
        </div>
    );
}